import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
    AnalyticsDailyResponse,
    AnalyticsEventRequest,
    KeyCountRow,
    OrgBusinessCardAnalyticsSummary,
    OrgBusinessCardDailyAnalytics,
    OrgBusinessCardTopCard
} from '../model/analytics-models';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AnalyticsApiService {

    private readonly baseUrl = environment.cognitiveServiceUrl;

    public constructor(private readonly http: HttpClient) {}

    // Capture event
    public captureEvent(cardId: string, overrides: Partial<AnalyticsEventRequest> = {}) {
        const payload: AnalyticsEventRequest = {
            cardId,
            eventType: 'view',
            source: 'digital-card',
            deviceType: getDeviceType(),
            userAgent: navigator.userAgent,
            referrerUrl: document.referrer || undefined,
            visitorId: getOrCreateVisitorId(),
            ...overrides
        };

        return this.http.post<void>(`${this.baseUrl}/Analytics/event`, payload);
    }

    // Summary
    public getSummaryInRange(cardId: string, startUtc?: Date, endUtc?: Date) {

        let params = new HttpParams();
        if (startUtc) params = params.set('startUtc', startUtc.toISOString());
        if (endUtc) params = params.set('endUtc', endUtc.toISOString());

        return this.http
        .get<any>(`${this.baseUrl}/Analytics/cards/${encodeURIComponent(cardId)}/summary/range`, { params })
        .pipe(
            map(res => ({
                totalViews: res.totalViews,
                uniqueVisitors: res.uniqueVisitors,
                downloads: res.downloads,
                addContactOpened: res['add-contact-opened'],
                contactSaved: res['contact-saved']
            }))
        );
    }

    // Devices by range
    public getDevicesInRange(cardId: string, startUtc?: Date, endUtc?: Date) {

        let params = new HttpParams();
        if (startUtc) params = params.set('startUtc', startUtc.toISOString());
        if (endUtc) params = params.set('endUtc', endUtc.toISOString());

        return this.http.get<KeyCountRow[]>(
            `${this.baseUrl}/Analytics/cards/${encodeURIComponent(cardId)}/devices/range`,{ params }
        );
    }

    // Daily rollups
    public getDailyInRange(cardId: string, start?: Date, end?: Date) {

        let params = new HttpParams();
        if (start) params = params.set('start', toYmd(start));
        if (end) params = params.set('end', toYmd(end));

        return this.http.get<AnalyticsDailyResponse[]>(
            `${this.baseUrl}/Analytics/cards/${encodeURIComponent(cardId)}/daily`,{ params }
        );
    }

    // Referrers
    public getReferrersInRange(cardId: string, startUtc?: Date, endUtc?: Date) {

        let params = new HttpParams();
        if (startUtc) params = params.set('startUtc', startUtc.toISOString());
        if (endUtc) params = params.set('endUtc', endUtc.toISOString());

        return this.http.get<KeyCountRow[]>(
            `${this.baseUrl}/Analytics/cards/${encodeURIComponent(cardId)}/referrers/range`,{ params }
        );
    }

    public getOrganizationSummaryInRange(orgId: number | string, startUtc?: Date, endUtc?: Date) {
        const params = this.buildUtcRangeParams(startUtc, endUtc);

        return this.http
            .get<Record<string, number>>(`${this.baseUrl}/Analytics/organizations/${encodeURIComponent(String(orgId))}/summary/range`, { params })
            .pipe(map(res => this.mapOrgSummary(res)));
    }

    public getOrganizationDailyInRange(orgId: number | string, start?: Date, end?: Date) {
        let params = new HttpParams();
        if (start) params = params.set('start', toYmd(start));
        if (end) params = params.set('end', toYmd(end));

        return this.http.get<OrgBusinessCardDailyAnalytics[]>(
            `${this.baseUrl}/Analytics/organizations/${encodeURIComponent(String(orgId))}/daily`,
            { params }
        );
    }

    public getOrganizationDevicesInRange(orgId: number | string, startUtc?: Date, endUtc?: Date) {
        const params = this.buildUtcRangeParams(startUtc, endUtc);

        return this.http.get<KeyCountRow[]>(
            `${this.baseUrl}/Analytics/organizations/${encodeURIComponent(String(orgId))}/devices/range`,
            { params }
        );
    }

    public getOrganizationReferrersInRange(orgId: number | string, startUtc?: Date, endUtc?: Date) {
        const params = this.buildUtcRangeParams(startUtc, endUtc);

        return this.http.get<KeyCountRow[]>(
            `${this.baseUrl}/Analytics/organizations/${encodeURIComponent(String(orgId))}/referrers/range`,
            { params }
        );
    }

    public getOrganizationTopCardsInRange(orgId: number | string, startUtc?: Date, endUtc?: Date, limit: number = 5) {
        const params = this.buildUtcRangeParams(startUtc, endUtc).set('top', String(limit));

        return this.http.get<OrgBusinessCardTopCard[]>(
            `${this.baseUrl}/Analytics/organizations/${encodeURIComponent(String(orgId))}/top-cards/range`,
            { params }
        );
    }

    private buildUtcRangeParams(startUtc?: Date, endUtc?: Date): HttpParams {
        let params = new HttpParams();
        if (startUtc) params = params.set('startUtc', startUtc.toISOString());
        if (endUtc) params = params.set('endUtc', endUtc.toISOString());
        return params;
    }

    private mapOrgSummary(res: Record<string, number>): OrgBusinessCardAnalyticsSummary {
        return {
            totalViews: res?.totalViews ?? 0,
            uniqueVisitors: res?.uniqueVisitors ?? 0,
            downloads: res?.downloads ?? 0,
            addContactOpened: res?.['add-contact-opened'] ?? res?.addContactOpened ?? 0,
            contactSaved: res?.['contact-saved'] ?? res?.contactSaved ?? 0,
            totalCards: res?.totalCards ?? 0,
            activeCards: res?.activeCards ?? 0,
            inactiveCards: res?.inactiveCards ?? 0,
            cardsCreated: res?.cardsCreated ?? 0,
            previousTotalViews: res?.previousTotalViews ?? 0,
            previousUniqueVisitors: res?.previousUniqueVisitors ?? 0,
            previousDownloads: res?.previousDownloads ?? 0,
            previousAddContactOpened: res?.previousAddContactOpened ?? 0,
            previousContactSaved: res?.previousContactSaved ?? 0,
            previousCardsCreated: res?.previousCardsCreated ?? 0
        };
    }

}

// Helpers
function getOrCreateVisitorId(): string {
    const key = 'visitorId';
    let id = localStorage.getItem(key);
    if (!id) {
        id = (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `${Date.now()}-${Math.random()}`;
        localStorage.setItem(key, id);
    }
    return id;
}

function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const ua = navigator.userAgent || '';
    if (/iPad|Tablet/i.test(ua)) return 'tablet';
    if (/Mobile|Android|iPhone|iPod/i.test(ua)) return 'mobile';
    return 'desktop';
}

function toYmd(d: Date): string {
    return d.toISOString().slice(0, 10);
}
